import { Button, Center, Stack, Text } from '@mantine/core';
import { IconAlertTriangle, IconRefresh } from '@tabler/icons-react';

/**
 * FailedLoadingScreen - A reusable error state component with customizable messaging
 *
 * A professional error screen component that displays an error icon with
 * customizable error messages and an optional retry action. Designed to provide
 * consistent error states across the application.
 *
 * @param {Object} props - Component props
 * @param {string} [props.title='Failed to load data'] - Primary error message
 * @param {string} [props.description='Something went wrong while loading your data. Please try again.'] - Secondary descriptive text
 * @param {string} [props.height='100vh'] - Height of the error container
 * @param {Function} [props.onRetry] - Optional retry function to call when retry button is clicked
 * @param {string} [props.retryLabel='Try Again'] - Label for the retry button
 * @param {boolean} [props.showRetryButton=true] - Whether to show the retry button
 * @param {string} [props.iconColor='red'] - Color of the error icon
 *
 * @example
 * // Basic usage with default props
 * <FailedLoadingScreen />
 *
 * @example
 * // Custom error message only
 * <FailedLoadingScreen title="Failed to save changes" />
 *
 * @example
 * // Custom message and description
 * <FailedLoadingScreen
 *   title="Failed to load invoices"
 *   description="We couldn't retrieve your invoice data. Please check your connection."
 * />
 *
 * @example
 * // With retry functionality
 * <FailedLoadingScreen
 *   title="Failed to load vendors"
 *   description="Unable to fetch vendor data from the server."
 *   onRetry={() => fetchVendors()}
 *   retryLabel="Reload Vendors"
 * />
 *
 * @example
 * // Without retry button
 * <FailedLoadingScreen
 *   title="Access Denied"
 *   description="You don't have permission to view this data."
 *   showRetryButton={false}
 * />
 *
 * @example
 * // Full customization
 * <FailedLoadingScreen
 *   title="Connection Error"
 *   description="Please check your internet connection and try again."
 *   height="50vh"
 *   iconColor="orange"
 *   retryLabel="Reconnect"
 *   onRetry={handleReconnect}
 * />
 */
const FailedLoadingScreen = ({
  title = 'Error loading data',
  description = 'Something went wrong while loading data. Please try again.',
  height = '100vh',
  onRetry,
  retryLabel = 'Try Again',
  showRetryButton = true,
  iconColor = 'red',
}) => {
  return (
    <Center style={{ height }}>
      <Stack spacing="md" align="center" style={{ maxWidth: 480 }}>
        <IconAlertTriangle size={48} color={iconColor} />
        <Stack spacing="xs" align="center">
          <Text size="lg" color="red" weight={600} align="center">
            {title}
          </Text>
          <Text size="sm" color="dimmed" align="center">
            {description}
          </Text>
        </Stack>
        {showRetryButton && onRetry && (
          <Button
            leftIcon={<IconRefresh size={16} />}
            variant="light"
            color="red"
            onClick={onRetry}>
            {retryLabel}
          </Button>
        )}
      </Stack>
    </Center>
  );
};

export default FailedLoadingScreen;
